import { useState, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { TableRow, TableCell, Checkbox } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import TeeBoxMappingModal from 'components/form/course/TeeBoxMappingModal'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import SimpleTable from 'components/ui/SimpleTable'
import DateTimePicker from '../DateTimePicker'
import { selectCourse } from 'store/course/slice'
import { editCourseVersion, setCourseVersionOfficial, setCourseVersionReviewed } from 'store/course/thunks'
import { selectLocale } from 'store/locale/slice'
import { convertDateTimeToUTC, formatDateTime, localizedDateTime } from 'utils/formatDate'
import SearchButton from '../SearchButton'

interface Props {
  course: Course
  version: CourseVersion | null
  changeSelectedVersion: (cv: CourseVersion) => void
}

export default function CourseVersions({ course, version, changeSelectedVersion }: Props): JSX.Element {
  const [visibleCount, setVisibleCount] = useState<number>(5)
  const [teeBoxDialogOpen, setTeeBoxDialogOpen] = useState<boolean>(false)
  const [dispatchingCourseVersionId, setDispatchingCourseVersionId] = useState<number | null>(null)
  const [previousOfficialVersion, setPreviousOfficialVersion] = useState<CourseVersion | null>(null)
  const [editActiveTimeForVersion, setEditActiveTimeForVersion] = useState<CourseVersion | null>(null)
  const intl = useIntl()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const startTimeRef = useRef<HTMLInputElement | undefined>(undefined)
  const { versionOfficialSaveStatus, versionSaveStatus } = useSelector(selectCourse)
  const isCourseVersionEditAllowed = course.provider?.options?.courseVersionEditAllowed === true

  const officialVersion = useMemo(() => {
    const versions = course.versions || []
    const officialVersions = versions.filter((cv: CourseVersion) => cv.isOfficial)
    return officialVersions.length > 0 ? officialVersions[0] : null
  }, [course.versions])

  useEffect(() => {
    const versions = course.versions || []
    const officialVersionIndex = versions.findIndex((cv: CourseVersion) => cv.isOfficial)
    const newVisibleCount = versions.length - officialVersionIndex
    if (officialVersionIndex >= 0 && newVisibleCount > 5) {
      setVisibleCount(newVisibleCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isValidCourseMapping = course.provider?.options?.generateCourseVersionMapping === true
    if (versionOfficialSaveStatus === 'succeeded' && isValidCourseMapping) {
      setTeeBoxDialogOpen(true)
    }
  }, [versionOfficialSaveStatus, course.provider?.options])

  const getCourseVersions = () => {
    return course.versions
      ? course.versions
          .slice()
          .sort((a: CourseVersion, b: CourseVersion) => b.version - a.version)
          .splice(0, visibleCount)
      : []
  }

  const columns: TableColumn[] = [
    {
      id: 'version',
      label: intl.formatMessage({ id: 'strings.version' }),
    },
    {
      id: 'creatorName',
      label: intl.formatMessage({ id: 'strings.creatorName' }),
    },
    {
      id: 'creatorEmail',
      label: intl.formatMessage({ id: 'strings.creatorEmail' }),
    },
    {
      id: 'status',
      label: intl.formatMessage({ id: 'strings.status' }),
    },
    {
      id: 'created',
      label: intl.formatMessage({ id: 'strings.created' }),
    },
    {
      id: 'provider',
      label: intl.formatMessage({ id: 'strings.provider' }),
    },
    {
      id: 'view',
      label: '',
    },
    { id: 'reviewed', label: intl.formatMessage({ id: 'strings.reviewed' }) },
    {
      id: 'official',
      label: '',
    },
    {
      id: 'activeTimePeriod',
      label: intl.formatMessage({ id: 'strings.activeTimePeriod' }),
    },
  ]

  const getVersionStatus = (cv: CourseVersion) => {
    return cv.isOfficial
      ? intl.formatMessage({ id: 'strings.official' })
      : intl.formatMessage({ id: 'strings.unofficial' })
  }

  const getCreatedDate = (cv: CourseVersion) => {
    const date = new Date(cv.created.date)
    return localizedDateTime(date, locale.language.code)
  }

  const getMakeOfficialLinkText = (cv: CourseVersion) => {
    const make = intl.formatMessage({ id: 'strings.make' })
    const official = intl.formatMessage({ id: 'strings.official' })
    return !cv.isOfficial && hasNotBeenOfficialBefore(cv) && isAllowedProvider(cv) ? `${make} ${official}` : ''
  }

  const makeVersionOfficial = (cv: CourseVersion) => {
    setPreviousOfficialVersion(officialVersion)
    dispatch(setCourseVersionOfficial(cv.id))
  }

  const showMoreVersions = () => {
    setVisibleCount(visibleCount + 5)
  }

  const renderStartTimePicker = (cv: CourseVersion) => {
    const startTime = cv.activeStartTime ? formatDateTime(new Date(cv.activeStartTime?.date)) : undefined

    return (
      <StartTimeEditContainer>
        <DateTimePicker
          name="startTime"
          label={intl.formatMessage({ id: 'strings.startTime' })}
          initialValue={startTime}
          showSeconds
          inputRef={startTimeRef}
        />
        <SaveTimeRangeButton
          text={intl.formatMessage({ id: 'strings.save' })}
          status={versionSaveStatus}
          onClick={() => submitCourseVersionEdit(cv)}
        />
        <CancelTimeRangeSave onClick={() => setEditActiveTimeForVersion(null)} />
      </StartTimeEditContainer>
    )
  }

  const submitCourseVersionEdit = (cv: CourseVersion) => {
    const startTime = startTimeRef.current?.value ? new Date(startTimeRef.current.value) : undefined
    const startTimeString = startTime ? convertDateTimeToUTC(startTime) : undefined

    const payload: CourseVersionEditPayload = {
      courseVersionId: cv.id,
      data: { versionActiveStartTime: startTimeString },
    }
    dispatch(editCourseVersion(payload))
  }

  const getVersionActiveRange = (cv: CourseVersion) => {
    const { activeStartTime, activeEndTime } = cv
    const start = activeStartTime ? new Date(activeStartTime.date) : null
    const end = activeEndTime ? new Date(activeEndTime.date) : null
    const startTime = start ? localizedDateTime(start, locale.language.code) : ''
    const endTime = end ? localizedDateTime(end, locale.language.code) : ''
    const range = `${startTime} - ${endTime}`
    if (editActiveTimeForVersion === cv) {
      return renderStartTimePicker(cv)
    }

    return isCourseVersionEditAllowed && cv.isOfficial && (startTime || endTime) ? (
      <>
        {range} <EditActiveTime onClick={() => setEditActiveTimeForVersion(cv)} />
      </>
    ) : (
      range
    )
  }

  const hasNotBeenOfficialBefore = (cv: CourseVersion) => {
    return cv.activeStartTime === null && cv.activeEndTime === null
  }

  const isAllowedProvider = (cv: CourseVersion) => {
    const providersMatch = cv.provider?.id === course.provider?.id
    const versionWithoutProvider = !cv.provider
    const modificationAllowed = course.provider?.options?.modificationAllowed === true || !course.provider
    return modificationAllowed && (providersMatch || versionWithoutProvider)
  }

  const canBeSetOfficial = (cv: CourseVersion) => {
    return cv.version === version?.version && !cv.isOfficial && hasNotBeenOfficialBefore(cv) && isAllowedProvider(cv)
  }

  const handleCheckChange = async (courseVersionId: number, checked: boolean) => {
    setDispatchingCourseVersionId(courseVersionId)
    dispatch(setCourseVersionReviewed({ id: courseVersionId, data: { reviewed: checked } }))
    setDispatchingCourseVersionId(null)
  }

  const renderRow = (cv: CourseVersion, i: number): JSX.Element => {
    const TableRowElement = cv.isOfficial ? TableRowOfficial : TableRow
    return (
      <TableRowElement hover key={i}>
        <TableCell>{cv.version}</TableCell>
        <TableCell>{cv.creatorName || '-'}</TableCell>
        <TableCell>{cv.creatorEmail || '-'}</TableCell>
        <TableCell>{getVersionStatus(cv)}</TableCell>
        <TableCell>{getCreatedDate(cv)}</TableCell>
        <TableCell>{cv.provider?.abbreviation || '-'}</TableCell>
        <TableCell>
          {cv.version === version?.version ? (
            <span>{intl.formatMessage({ id: 'strings.view' })}</span>
          ) : (
            <ActionText onClick={() => changeSelectedVersion(cv)}>
              {intl.formatMessage({ id: 'strings.view' })}
            </ActionText>
          )}
        </TableCell>
        <TableCell>
          <Checkbox
            checked={cv.reviewed}
            onChange={(e) => handleCheckChange(cv.id, e.target.checked)}
            disabled={dispatchingCourseVersionId === cv.id}
          />
        </TableCell>
        <TableCell>
          {canBeSetOfficial(cv) ? (
            <>
              <ActionText onClick={() => makeVersionOfficial(cv)}>{getMakeOfficialLinkText(cv)}</ActionText>
              {versionOfficialSaveStatus === 'loading' && <LoadingSpinnerGreen />}
            </>
          ) : (
            <span>{getMakeOfficialLinkText(cv)}</span>
          )}
        </TableCell>
        <TableCell>{getVersionActiveRange(cv)}</TableCell>
      </TableRowElement>
    )
  }

  return (
    <>
      <CourseVersionsContainer>
        <SectionTitle>{intl.formatMessage({ id: 'strings.courseVersions' })}</SectionTitle>
        <SimpleTable columns={columns}>
          {getCourseVersions().map((cv: CourseVersion, i: number) => renderRow(cv, i))}
        </SimpleTable>
        {course.versions && course.versions.length > visibleCount && (
          <LoadMore onClick={showMoreVersions}>{intl.formatMessage({ id: 'strings.loadMore' })}</LoadMore>
        )}
      </CourseVersionsContainer>
      {teeBoxDialogOpen && version && previousOfficialVersion && (
        <TeeBoxMappingModal
          open={teeBoxDialogOpen}
          close={() => setTeeBoxDialogOpen(false)}
          course={course}
          activeCourseVersion={version}
          previousCourseVersion={previousOfficialVersion}
        />
      )}
    </>
  )
}

const CourseVersionsContainer = styled.div`
  padding: 0 20px;

  & tbody tr {
    &:nth-child(even) {
      background-color: ${(props) => props.theme.customPalette.lightGray};
    }
  }
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  color: ${(props) => props.theme.palette.primary.main};
  cursor: pointer;
  margin-top: 20px;
  font-weight: 600;
`

const ActionText = styled.span`
  color: ${(props) => props.theme.palette.primary.main};
  cursor: pointer;
`

const EditActiveTime = styled(EditIcon)`
  color: ${(props) => props.theme.palette.primary.main};
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const StartTimeEditContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const SaveTimeRangeButton = styled(SearchButton)`
  height: 50px;
`

const CancelTimeRangeSave = styled(CancelIcon)`
  color: ${(props) => props.theme.customPalette.dark};
  cursor: pointer;
  margin-left: 5px;
`

const TableRowOfficial = styled(TableRow)`
  & td {
    font-weight: 800;
  }
`
